import { useEffect, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { Box, Flex } from '@chakra-ui/react';
import {
  errorMessageEmailInvalid,
  errorMessagePhoneNumberInvalid,
  errorMessageRecaptchaFailed,
  errorMessageZipCode,
} from 'constants/validation';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Input,
  Recaptcha,
  SimpleGrid,
  Spinner,
  Textarea,
} from 'components/UI';
import { checkIfError, validateForm } from 'utils/helpers';
import { validateRequired, validateEmail, validatePostalCode } from 'utils/validation';
import { ThemeContext } from 'contexts';
import { ISettings } from 'types';
import { useFetchCountries, useFetchCountryStates, useToast } from 'hooks';
import { getCountryRequiredFields } from 'utils/countries';
import RegionFields from 'components/common/RegionFields';
import PrivacyPolicy from 'components/common/PrivacyPolicy';
import { otherCountry } from 'constants/countryData';

export type OrderFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  country: string;
  state: string;
  zipCode: string;
  recaptcha?: string;
  specialRequests?: string;
};

type OrderFormProps = {
  onSubmit: (
    values: OrderFormValues | Record<string, never>,
    actions: FormikHelpers<OrderFormValues | Record<string, never>>
  ) => void;
  initialValues?: OrderFormValues | Record<string, never>;
  error?: string;
  buttonText?: string;
  data?: OrderFormValues | null;
};

const OrderForm = ({ onSubmit, initialValues = {}, data, buttonText = 'SUBMIT' }: OrderFormProps) => {
  const [submitted, setSubmitted] = useState(false);

  const [showToast] = useToast();

  const [fetchCountries, { loading: fetchCountriesLoading, error: fetchCountriesError, data: countriesData }] =
    useFetchCountries();

  const [
    fetchCountryStates,
    { loading: fetchCountryStatesLoading, data: countryStatesData, error: countryStatesError },
  ] = useFetchCountryStates();

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (!data?.country) return;

    fetchCountryStates(data.country);
  }, [data]);

  useEffect(() => {
    if (!countryStatesError) return;

    showToast({
      message: countryStatesError,
      status: 'error',
    });
  }, [countryStatesError]);

  if (checkIfError(countriesData))
    return (
      <Alert status="error" w="auto" borderRadius={4} variant="solid">
        <AlertIcon />
        <AlertDescription maxWidth="sm">{fetchCountriesError}</AlertDescription>
      </Alert>
    );

  const renderForm =
    fetchCountriesLoading || !countriesData ? (
      <Flex justifyContent="center" alignItems="center">
        <Spinner size="lg" />
      </Flex>
    ) : (
      <ThemeContext.Consumer>
        {(theme: ISettings | null) => (
          <Formik
            initialValues={
              data ??
              initialValues ?? {
                firstName: '',
                lastName: '',
                email: '',
                company: '',
                phone: '',
                country: '',
                state: '',
                zipCode: '',
              }
            }
            validate={async (values) => {
              const countryRequiredFields = getCountryRequiredFields(values.country);

              const errors = validateRequired<OrderFormValues | Record<string, never>>(
                ['firstName', 'lastName', 'email', 'company', 'phone', 'country', ...countryRequiredFields],
                values
              );

              if (values.email && !validateEmail(values.email)) {
                errors.email = errorMessageEmailInvalid;
              }

              if (!values.recaptcha) {
                errors.recaptcha = errorMessageRecaptchaFailed;
              }

              if (
                values.phone &&
                values.phone.replace(/\D/g, '').length > 0 &&
                values.phone.split('x')[0].replace(/\D/g, '').length < 10
              ) {
                errors.phone = errorMessagePhoneNumberInvalid;
              }

              if (values.zipCode && !validatePostalCode(values.country, values.zipCode)) {
                errors.zipCode = errorMessageZipCode;
              }

              return errors;
            }}
            onSubmit={onSubmit}
          >
            {(formConfig) => {
              const { values, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue } = formConfig;

              const formErrors = validateForm<OrderFormValues | Record<string, never>>({
                ...formConfig,
                submitted,
              });

              const otherCountryArr =
                theme?.otherCountry === 'true' ? [{ name: otherCountry.label, iso2: otherCountry.value }] : [];

              return (
                <form onSubmit={handleSubmit}>
                  <SimpleGrid columns={2} spacing={4} mb={4}>
                    <Box>
                      <FormControl isInvalid={!!formErrors.email}>
                        <FormLabel htmlFor="email">EMAIL *</FormLabel>
                        <Input
                          type="email"
                          name="email"
                          maxLength={100}
                          value={values.email}
                          error={formErrors.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Enter your email"
                          autoComplete="off"
                        />
                      </FormControl>
                    </Box>
                    <Box>
                      <FormControl isInvalid={!!formErrors.company}>
                        <FormLabel htmlFor="company">COMPANY *</FormLabel>
                        <Input
                          name="company"
                          maxLength={100}
                          error={formErrors.company}
                          value={values.company}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Enter your company"
                        />
                      </FormControl>
                    </Box>
                    <Box>
                      <FormControl isInvalid={!!formErrors.firstName}>
                        <FormLabel htmlFor="firstName">FIRST NAME *</FormLabel>
                        <Input
                          name="firstName"
                          maxLength={100}
                          value={values.firstName}
                          error={formErrors.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Enter your first name"
                        />
                      </FormControl>
                    </Box>
                    <Box>
                      <FormControl isInvalid={!!formErrors.lastName}>
                        <FormLabel htmlFor="lastName">LAST NAME *</FormLabel>
                        <Input
                          name="lastName"
                          maxLength={100}
                          value={values.lastName}
                          error={formErrors.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Enter your last name"
                        />
                      </FormControl>
                    </Box>
                    <Box>
                      <FormControl isInvalid={!!formErrors.phone}>
                        <FormLabel htmlFor="phone">PHONE *</FormLabel>
                        <Input
                          name="phone"
                          mask="(999) 999-9999 x 9999"
                          error={formErrors.phone}
                          placeholder="(999) 999-9999"
                          value={values.phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </FormControl>
                    </Box>
                    <RegionFields
                      {...formConfig}
                      countryStatesLoading={!!fetchCountryStatesLoading}
                      countries={[
                        ...(countriesData ?? [])
                          .filter(({ iso2 }) => theme?.countries.includes(iso2) || data?.country === iso2)
                          .sort((a, b) =>
                            !theme?.countries ? 1 : theme.countries.indexOf(a.iso2) - theme.countries.indexOf(b.iso2)
                          ),
                        ...otherCountryArr,
                      ]}
                      values={values as OrderFormValues}
                      errors={formErrors}
                      countryStates={!checkIfError(countryStatesData) ? countryStatesData ?? [] : []}
                      onChangeCountry={(country: string) => fetchCountryStates(country)}
                    />

                    <Box gridColumn="span 2">
                      <FormLabel htmlFor="specialRequests">SPECIAL REQUESTS</FormLabel>

                      <Textarea
                        name="specialRequests"
                        value={values.specialRequests}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter any special requests."
                        maxLength={500}
                      />
                    </Box>
                  </SimpleGrid>
                  <FormControl isInvalid={!!formErrors.recaptcha}>
                    <Recaptcha
                      onChange={(token: string) => setFieldValue('recaptcha', token)}
                      error={formErrors.recaptcha}
                    />
                  </FormControl>

                  <PrivacyPolicy policy={theme?.privacyPolicy} />
                  <Flex justifyContent="flex-end">
                    <Button
                      isDisabled={isSubmitting}
                      isLoading={isSubmitting}
                      onClick={() => setSubmitted(true)}
                      type="submit"
                    >
                      {buttonText}
                    </Button>
                  </Flex>
                </form>
              );
            }}
          </Formik>
        )}
      </ThemeContext.Consumer>
    );

  return renderForm;
};

export default OrderForm;
